import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { todayISO } from "@/lib/dateUtils";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import ProjectPicker from "@/components/ProjectPicker";

export default function EditEntryDialog({
  entry,
  onClose,
  onSaved,
  previewMode = false,
  onPreviewSave,
}) {
  const [date, setDate] = useState("");
  const [hours, setHours] = useState("");
  const [activity, setActivity] = useState("");
  const [projectId, setProjectId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (entry) {
      setDate(entry.date);
      setHours(String(entry.hours));
      setActivity(entry.activity);
      setProjectId(entry.project_id || null);
      setError("");
    }
  }, [entry]);

  if (!entry) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const h = parseFloat(hours);
    if (!date) return setError("Date is required.");
    if (!Number.isFinite(h) || h <= 0 || h > 24)
      return setError("Hours must be between 0 and 24.");
    if (!activity.trim()) return setError("Activity is required.");

    setSubmitting(true);
    try {
      if (previewMode) {
        await onPreviewSave?.({
          date,
          hours: h,
          activity: activity.trim(),
          project_id: projectId || null,
        });
        setSubmitting(false);
      } else {
        await api.put(`/entries/${entry.id}`, {
          date,
          hours: h,
          activity: activity.trim(),
          project_id: projectId || null,
        });
        setSubmitting(false);
        toast.success("Entry updated");
        onSaved?.();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not save changes.";
      setError(msg);
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      onClick={() => !submitting && onClose?.()}
      data-testid="edit-entry-dialog"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Edit entry</h3>
          <button
            onClick={() => !submitting && onClose?.()}
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
              <input
                type="date"
                value={date}
                max={todayISO()}
                onChange={(e) => setDate(e.target.value)}
                data-testid="edit-date-input"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2"
                style={{ "--tw-ring-color": BRAND.teal }}
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Hours</label>
              <input
                type="number"
                step="any"
                min="0.01"
                max="24"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                data-testid="edit-hours-input"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2"
                style={{ "--tw-ring-color": BRAND.teal }}
                disabled={submitting}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Project</label>
            <ProjectPicker
              value={projectId}
              onChange={setProjectId}
              disabled={submitting}
              testId="edit-project-picker"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Activity</label>
            <textarea
              rows={3}
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              data-testid="edit-activity-input"
              maxLength={500}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 resize-none"
              style={{ "--tw-ring-color": BRAND.teal }}
              disabled={submitting}
            />
          </div>
          {error && (
            <div
              className="text-sm px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700"
              data-testid="edit-error"
            >
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => !submitting && onClose?.()}
              className="px-4 py-2 rounded-lg text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="edit-save-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition disabled:opacity-70"
              style={{
                background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
              }}
            >
              {submitting && <Loader2 className="animate-spin" size={14} />}
              Save changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
